/**
 * useSectionTheme.js
 * -----------------------------------------------------------------
 * Hook source de verite de la section active de l'app ColoBongo.
 *
 * Consulte useLocation() de react-router-dom et retourne un objet
 * de tokens CSS pour la section courante. Coherent avec les regles
 * de matching des onglets de BottomNav.js.
 *
 * Sections : accueil (peche), mur (sky), studio (lavender), frigo (sage).
 * Le FAB Scan ne change jamais : pas de section "scan" exposee ici.
 *
 * Pas de Context React necessaire : useLocation() est deja disponible
 * partout dans l'arbre Router. Le hook est appele directement dans
 * BottomNav, CozyPageWrapper et les pages.
 *
 * Reference : CODEX_PROMPT_55 (variante B, validee 2026-05-25).
 * -----------------------------------------------------------------
 */

import { useLocation } from 'react-router-dom';

// Correspondance exacte route -> cle de section.
// Doit rester coherent avec les `matches` definis dans BottomNav.js.
const ROUTE_TO_SECTION = {
  '/':        'accueil',
  '/mur':     'mur',
  '/showcase/coloring-of-the-month': 'mur',
  '/studio':  'studio',
  '/palettes':      'studio',
  '/light-helper':  'studio',
  '/textures':      'studio',
  '/gallery': 'frigo',
};

// Sections dont la detection se fait par prefixe (startsWith).
// Utilise apres l'echec d'une correspondance exacte.
const SECTION_PREFIXES = [
  { prefix: '/studio/',   section: 'studio' },
  { prefix: '/textures/', section: 'studio' },
];

// Tokens CSS retournes par section. Les valeurs pointent vers les
// variables CSS definies dans frontend/src/index.css (--section-*-*).
const SECTION_TOKENS = {
  accueil: {
    key:    'accueil',
    text:   'var(--section-accueil-text)',
    bg:     'var(--section-accueil-bg)',
    accent: 'var(--section-accueil-accent)',
    tint:   'var(--section-accueil-tint)',
    border: 'var(--section-accueil-border)',
    cardBg: 'var(--cb-peach-100)',
  },
  mur: {
    key:    'mur',
    text:   'var(--section-mur-text)',
    bg:     'var(--section-mur-bg)',
    accent: 'var(--section-mur-accent)',
    tint:   'var(--section-mur-tint)',
    border: 'var(--section-mur-border)',
    cardBg: 'var(--cb-sky-100)',
  },
  studio: {
    key:    'studio',
    text:   'var(--section-studio-text)',
    bg:     'var(--section-studio-bg)',
    accent: 'var(--section-studio-accent)',
    tint:   'var(--section-studio-tint)',
    border: 'var(--section-studio-border)',
    cardBg: 'var(--cb-lavender-100)',
  },
  frigo: {
    key:    'frigo',
    text:   'var(--section-frigo-text)',
    bg:     'var(--section-frigo-bg)',
    accent: 'var(--section-frigo-accent)',
    tint:   'var(--section-frigo-tint)',
    border: 'var(--section-frigo-border)',
    cardBg: 'var(--cb-sage-100)',
  },
};

// Fallback neutre : si la route ne correspond a aucune section connue,
// on retourne peche (accueil) comme defaut. Ce fallback ne s'applique
// qu'au liseré/tint du wrapper de page - les surfaces flottantes
// (modals, toasts, drawers, portals) ne consomment pas ces tokens et
// restent toujours creme.
const FALLBACK_SECTION = SECTION_TOKENS.accueil;

export function useSectionTheme() {
  const { pathname } = useLocation();

  // Correspondance exacte en premier
  const exactKey = ROUTE_TO_SECTION[pathname];
  if (exactKey) return SECTION_TOKENS[exactKey];

  // Puis prefixes (pour les sous-routes dynamiques de Studio/Textures)
  for (const { prefix, section } of SECTION_PREFIXES) {
    if (pathname.startsWith(prefix)) return SECTION_TOKENS[section];
  }

  return FALLBACK_SECTION;
}

export default useSectionTheme;
